// What a new page starts as. `welcome` is a page about itself, which also
// exercises every component in the library; the others are the test
// fixtures, opened with ?fixture=<name>.
import type { Spec } from "./doc";

export const welcome: Spec[] = [
  { type: "heading", props: { level: "1" }, text: "A page you can edit, together" },
  {
    type: "paragraph",
    text: "Open this page in a second tab or window and type in either: the other follows as you type. There is no server between them, and nothing leaves this browser.",
  },
  {
    type: "section",
    children: [
      { type: "heading", props: { level: "2" }, text: "Move anything" },
      { type: "paragraph", text: "Drag a block by its handle on the left, or press Alt and an arrow key. A move is one edit, so someone typing in the block keeps typing in it." },
    ],
  },
  {
    type: "callout",
    props: { tone: "note" },
    children: [{ type: "paragraph", text: "This is a callout: a component that holds other blocks. Drag a paragraph into it." }],
  },
  {
    type: "section",
    children: [
      { type: "heading", props: { level: "2" }, text: "It still works while you edit it" },
      { type: "paragraph", text: "The counter, the tabs and the form below are components with behaviour, from the app's source. Use them, then edit the words around them." },
      { type: "counter", props: { label: "Click me", start: 0 } },
      {
        type: "tabs",
        props: { labels: "First, Second" },
        children: [
          { type: "paragraph", text: "This is the first tab. Each tab holds blocks of its own." },
          { type: "paragraph", text: "This is the second tab. Switching is the component's own behaviour." },
        ],
      },
      { type: "signup", props: { label: "Email", button: "Sign up", required: true } },
    ],
  },
];

// Styles set on elements, tokens of the page's own, and marked-up text.
const styled: Spec[] = [
  { type: "heading", props: { level: "1" }, style: { color: "var(--accent)", "letter-spacing": "-0.5px" }, text: "A styled page" },
  {
    type: "paragraph",
    style: { "font-size": "19px", "line-height": "1.7" },
    delta: [
      { insert: "Words in " },
      { insert: "bold", attributes: { bold: true } },
      { insert: ", " },
      { insert: "italic", attributes: { italic: true } },
      { insert: ", " },
      { insert: "both", attributes: { bold: true, italic: true } },
      { insert: ", " },
      { insert: "code", attributes: { code: true } },
      { insert: " and " },
      { insert: "a link", attributes: { link: "https://example.com/" } },
      { insert: "." },
    ],
  },
  {
    type: "callout",
    props: { tone: "warn" },
    style: { padding: "var(--space-4)", "border-radius": "12px" },
    children: [{ type: "paragraph", style: { color: "var(--warn)" }, text: "A warning, padded by a token." }],
  },
  { type: "counter", props: { label: "Count up", start: 5 }, style: { "background-color": "var(--surface)", padding: "8px" } },
];
const styledTokens = { "--accent": "oklch(52% 0.2 300)", "--space-4": "40px" };

// What a merge can leave: an unknown component, props it doesn't have,
// text that looks like markup, and empty blocks.
const edge: Spec[] = [
  { type: "heading", props: { level: "2" }, text: "# Not a heading, *not* bold, <not> a tag, {not} an id \\ and a backslash" },
  { type: "paragraph", text: "" },
  { type: "paragraph", text: "  Leading spaces\nand a line break" },
  { type: "chart", props: { series: [1, 2, 3], title: "From a newer build" } },
  { type: "counter", props: { label: "Odd", start: "not a number", extra: true } },
  { type: "section", children: [] },
  { type: "callout", props: { tone: "note" }, children: [{ type: "callout", props: { tone: "warn" }, children: [{ type: "heading", props: { level: "3" }, text: "Nested" }] }] },
];

export const fixtures: Record<string, { spec: Spec[]; tokens?: Record<string, string> }> = {
  welcome: { spec: welcome },
  styled: { spec: styled, tokens: styledTokens },
  edge: { spec: edge },
};
